import { RenderLogDoc, RenderLogEntry } from './renderLogConstants';
import { useRenderLogStore } from 'zstore/renderLogStore';

const BUFFER_CAP = 1000;
const NOTIFY_THROTTLE_MS = 200;

let nextEntryId = 1;
// Appends mutate `entries` in place; `snapshot` is the immutable view handed to React and is only
// replaced when subscribers are notified. Copying the whole buffer on every append would cost a
// 1000-element copy per logged event, which is enough to distort the render timings being measured.
let entries: RenderLogEntry[] = [];
let snapshot: readonly RenderLogEntry[] = [];
const bufferListeners = new Set<() => void>();
let notifyTimer: ReturnType<typeof setTimeout> | undefined;

export function appendRenderLogEntry(doc: RenderLogDoc, event: string, detail = '') {
  if (useRenderLogStore.getState().paused) return;

  entries.push({
    id: nextEntryId++,
    ts: Date.now(),
    doc,
    event,
    detail,
  });
  if (entries.length > BUFFER_CAP) {
    entries.splice(0, entries.length - BUFFER_CAP);
  }
  scheduleBufferNotify();
}

export function clearRenderLogBuffer() {
  entries = [];
  publish();
}

export function getRenderLogSnapshot(): readonly RenderLogEntry[] {
  return snapshot;
}

export function subscribeRenderLogBuffer(listener: () => void): () => void {
  bufferListeners.add(listener);
  return () => bufferListeners.delete(listener);
}

function publish() {
  if (notifyTimer) {
    clearTimeout(notifyTimer);
    notifyTimer = undefined;
  }
  snapshot = entries.slice();
  bufferListeners.forEach((listener) => listener());
}

function scheduleBufferNotify() {
  if (notifyTimer) return;
  notifyTimer = setTimeout(publish, NOTIFY_THROTTLE_MS);
}

/** @internal Test helper */
export function resetRenderLogBufferForTests() {
  entries = [];
  snapshot = [];
  nextEntryId = 1;
  if (notifyTimer) {
    clearTimeout(notifyTimer);
    notifyTimer = undefined;
  }
}
